import React, { useState, useMemo } from "react";
import Calendar from "react-calendar";
import ReactLoading from "react-loading";
import "./adminCalendar.css";

import { useQuery, useQueryClient } from "react-query";
import { useAvailability } from "../../hooks/useAvailability.js";
import * as helpers from "../../utils/utils.js";

import SlotList from "../slotList/slotList.js";

const today = new Date();
const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

function AdminCalendar({  
  selectedDates, 
  addDate, 
  removeDate, 
  clearDates,}) {
  const legendItems = ["unavailable", "available", "booked", "partial"];
  const queryClient = useQueryClient();

  // fetches availability for the first available date
  const { fetchAvailability } = useAvailability();

  const [firstVisible, setFirstVisible] = useState(firstOfMonth);
  
  const { firstDay, lastDay } = useMemo(() => {
    const aligned = helpers.alignToSunday(firstVisible);
    return {
      firstDay: aligned,
      lastDay: aligned + 1000 * 60 * 60 * 24 * 43,
    };
  }, [firstVisible]);

  const queryKey = ["adminAvailability", firstDay, lastDay];

  const { 
    data: availability, 
    isLoading: availabilityIsLoading, 
    error: availabilityError 
  } = useQuery(
    queryKey,
    () => fetchAvailability({ firstDay, lastDay }),
    {
      staleTime: 1000 * 60 * 30,          // 30 minutes
      refetchOnWindowFocus: false,        // prevent re-fetch on tab switch
      cacheTime: 1000 * 60 * 60,          // 1 hour cache
    }
  );
    
  const fullAvailability = useMemo(() => {
    const cached = queryClient
      .getQueryCache()
      .findAll({ queryKey: ['availability'], exact: false })
      .flatMap((query) => query.state.data || []);

    // De-dupe by ID
    const seen = new Set();
    return [...cached, ...(availability || [])].filter((slot) => {
      if (seen.has(slot.id)) return false;
      seen.add(slot.id);
      return true;
    });
  }, [availability, queryClient]);
  

  // handles when a day on the calendar is selected or deselected
  function handleClickDay(value) {
    const clickedDay = helpers.toUtcMidnightTimestamp(value).valueOf();

    if (selectedDates.includes(clickedDay)) {
      // If the date is already selected, remove it
      removeDate(clickedDay);
    } else {
      addDate(clickedDay);
    }
  }

  /**
   * determines which classname to add to a tile depending on
   * availability of the date
   * @param {Date} date date of the tile
   * @returns {string} name of the class to be added
   */
  function updateCalenderTiles({ date }) {
    if (!availability) return "unavailable";
    
    const tileDate = helpers.toUtcMidnightTimestamp(date).valueOf();

    let status = "";

    if (selectedDates.includes(tileDate)) {
      status = "selected";
    }

    const dateSlots = availability.filter((slot) => slot.date === tileDate);
    const visibleSlots = dateSlots.filter((slot) => slot.status !== "unavailable");
    const bookedLikeSlots = visibleSlots.filter((slot) => slot.status === "booked" || slot.status === "pending");

    if (visibleSlots.length === 0) {
      status += " unavailable";
    } else if (visibleSlots.every((slot) => slot.status === "available")) {
      status += " available";
    } else if (bookedLikeSlots.length === visibleSlots.length) {
      status += " booked";
    } else if (bookedLikeSlots.length > 0) {
      status += " partial";
    } else {
      status += " unavailable";
    }

    return status;
  }

  function updateTileContent({ date }) {
    if (!availability) return "";
    
    const tileDate = helpers.toUtcMidnightTimestamp(date).valueOf();

    const todayBookedSlots = availability.filter(
      (slot) =>
        slot.date === tileDate &&
        (slot.status === "booked" || slot.status === "pending"),
    );

    if (todayBookedSlots.length > 0) {
      return <p>{todayBookedSlots.length} booked</p>;
    } else {
      return <p style={{ visibility: "hidden" }}>unavailable</p>;
    }
  }

  // function isTileDisabled({ date }) {
  //   if (!availability) return true;
  //   const tileDate = helpers.toUtcMidnightTimestamp(date).valueOf();

  //   const dateHasAvailability = availability.some((slot) => {
  //     return parseInt(slot.date) === tileDate;
  //   });

  //   if (dateHasAvailability) {
  //     return false;
  //   } else {
  //     return true;
  //   }
  // }


  if (availabilityError) return <p>Error Loading Availability</p>

  return (
    <>
    <div className="calendar-container main-border admin-calendar">
      <div className="calendar-instructions">
        <div>
          {/* hidden button for spacing */}
          <button className="btn hidden" disabled={true}>
            Clear
          </button>
        </div>
        <div>
          <p className="section-heading">Select Dates</p>
        </div>
        <div>
          <button
            className="clear-btn btn"
            onClick={() => {
              clearDates();
            }}
            disabled={selectedDates.length < 1}
          >
            Clear
          </button>
        </div>
      </div>
      <div className="calendar-element-container">
      {(availabilityIsLoading) && (
        <div className="flex-center">
            <ReactLoading type="spin" color="#6c757d" height={60} width={60} />
        </div>
      )}
      <Calendar
        tileClassName={updateCalenderTiles}
        tileContent={updateTileContent}
        tileDisabled={() => false} // Disable all tiles to allow custom content
        onClickDay={handleClickDay}
        activeStartDate={firstVisible}
        onActiveStartDateChange={({ activeStartDate, action }) => {
          if (action === 'next' || action === 'prev') {
            setFirstVisible(activeStartDate);
          } 
        }}
        minDetail={"month"}
        calendarType="gregory"
      />
      </div>
      <div className="calendar-legend">
        {legendItems.map((item) => (
          <div key={item} className="item">
            <div className={"secondary-border color " + item}>a</div>
            <div className="label">{item}</div>
          </div>
        ))}
      </div>
    </div>

    <SlotList 
    selectedDates={selectedDates}
    clearSlots={clearDates} 
    availableSlots={fullAvailability}
    removeDate={removeDate}
    />

    </>
  );
}

export default AdminCalendar;
